import { useEffect, useRef, useMemo, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast-v2'

import { assetUrl } from 'utils/url'
import { usePenetapanPredikatTableStore } from 'app/KinerjaShared/components/PenetapanPredikatTable/Provider'

import type { PenetapanPredikatKinerjaStoreItem } from 'app/KinerjaShared/stores/penetapan-predikat-kinerja-store'

export type SendActionProps = {
  children?: React.ReactNode
  confirmationText?: string
  disabled?: boolean
  id?: string
  isError?: boolean
  isSuccess?: boolean
  contents?: {
    loadingMessage?: string
    successMessage?: string
    errorMessage?: string
    title?: string | React.ReactNode
    description?: string | React.ReactNode
  }
  onSubmit?: (items: Array<PenetapanPredikatKinerjaStoreItem>) => void
  onSuccess?: () => void
}

// Default contents
const DEFAULT_TITLE = 'Kirim Predikat Kinerja?'
const DEFAULT_DESCRIPTION = 'Data tidak dapat diubah lagi.'
const DEFAULT_ERROR_MESSAGE = 'Gagal kirim Predikat Kinerja. Coba lagi.'
const DEFAULT_CONFIRMATION_TEXT =
  'SAYA SETUJU UNTUK MENGIRIM PENETAPAN PREDIKAT KINERJA'
const DEFAULT_SUCCESS_MESSAGE = 'Predikat Kinerja berhasil dikirim'
const DEFAULT_LOADING_MESSAGE = 'Mengirim Predikat Kinerja...'

export function SendAction({
  children,
  confirmationText = DEFAULT_CONFIRMATION_TEXT,
  disabled,
  id,
  isError,
  isSuccess,
  contents = {
    loadingMessage: DEFAULT_LOADING_MESSAGE,
    successMessage: DEFAULT_SUCCESS_MESSAGE,
    errorMessage: DEFAULT_ERROR_MESSAGE,
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  onSubmit,
  onSuccess,
}: SendActionProps) {
  const toast = useToast()

  const [step, setStep] = useState<
    'CONFIRMATION' | 'LOADING' | 'SUCCESS' | 'IDLE'
  >('IDLE')

  // Store the count in ref so it can be accessed in the success dialog
  // without depending on the selected items value
  const countRef = useRef(0)

  // -------------------------------------
  // Resolve items to send,
  // only include items that has predikat
  // -------------------------------------
  const { selectedItems, unselectAllItems = () => {} } =
    usePenetapanPredikatTableStore((store) => ({
      selectedItems: store.selectedItems,
      unselectAllItems: store.unselectAllItems,
    }))
  const itemsToSend = useMemo(
    () =>
      Array.from(selectedItems.values()).filter(
        (item) => item.predikat !== 'PREDIKAT_NOT_SET'
      ),
    [selectedItems]
  )
  const hasUneligibleItems = itemsToSend.length !== selectedItems.size

  // -------------------------------------
  // Handle submit mutation
  // -------------------------------------
  const handleSubmit = () => {
    setStep('LOADING')

    // Set the count in ref so it can be accessed in the success dialog
    countRef.current = itemsToSend.length

    onSubmit?.(itemsToSend)
  }

  const handleClose = () => {
    setStep('IDLE')

    // Reset the count
    countRef.current = 0
  }

  const handleSubmitFinished = () => {
    if (isSuccess) {
      setStep('SUCCESS')
      unselectAllItems()
      onSuccess?.()
    } else if (isError) {
      toast({
        message: contents.errorMessage,
      })
      handleClose()
    }
  }

  return (
    <>
      <Button
        id={id}
        color="blue"
        size="lg"
        className="space-x-1"
        disabled={itemsToSend.length === 0 || disabled || hasUneligibleItems}
        onClick={() => setStep('CONFIRMATION')}
      >
        {children}
      </Button>

      <ConfirmationDialog
        isOpen={step === 'CONFIRMATION'}
        onPrev={handleClose}
        onNext={handleSubmit}
        confirmationText={confirmationText}
        title={contents.title}
        description={contents.description}
      />

      <LoadingDialog
        isOpen={step === 'LOADING'}
        minDuration={3000}
        onNext={handleSubmitFinished}
        loadingMessage={contents.loadingMessage}
      />

      <SuccessDialog
        successMessage={contents.successMessage}
        isOpen={step === 'SUCCESS'}
        count={countRef.current}
        onNext={handleClose}
      />
    </>
  )
}

type ConfirmationDialogProps = {
  isOpen: boolean
  confirmationText?: string
  title?: string | React.ReactNode
  description?: string | React.ReactNode
  onPrev: () => void
  onNext: () => void
}
function ConfirmationDialog({
  isOpen,
  confirmationText = DEFAULT_CONFIRMATION_TEXT,
  title,
  description,
  onPrev,
  onNext,
}: ConfirmationDialogProps) {
  const [confirmation, setConfirmation] = useState('')

  const isEnabled = confirmation === confirmationText

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onPrev}
    >
      <DialogOverlay />
      <DialogContent
        aria-labelledby="kirim-pegawai-confirmation-dialog-title"
        aria-describedby="kirim-pegawai-confirmation-dialog-description"
      >
        <div className="p-6 space-y-4">
          <img
            src={assetUrl('/illustrations/img-publish-data.png')}
            alt=""
            width={84}
            height={84}
          />

          <Text
            variant="display-sm"
            as="h2"
            id="kirim-pegawai-confirmation-dialog-title"
          >
            {title}
          </Text>

          <Text
            variant="body-lg"
            color="subdued"
            id="kirim-pegawai-confirmation-dialog-description"
          >
            {description}
          </Text>
          <Text variant="body" color="subdued">
            Mohon ketik kalimat ini pada kolom kalimat persetujuan:
          </Text>
          <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
            <Text variant="heading-sm" color="inherit">
              {confirmationText}
            </Text>
          </div>
          <div className="space-y-1">
            <Text as="label" htmlFor="uncompleted-confirmation-text">
              Kalimat persetujuan
            </Text>
            <Textarea
              id="uncompleted-confirmation-text"
              placeholder="Ketik di sini."
              autoComplete="off"
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </div>

          <div className="flex space-x-3 !justify-start">
            <Button color="white" size="lg" onClick={onPrev}>
              Kembali
            </Button>
            <Button
              color="blue"
              disabled={!isEnabled}
              size="lg"
              type="button"
              onClick={() => onNext()}
            >
              Konfirmasi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

type LoadingDialogProps = {
  isOpen: boolean
  minDuration?: number // ms
  onNext: () => void
  loadingMessage?: string
}
function LoadingDialog({
  isOpen,
  minDuration,
  onNext,
  loadingMessage = DEFAULT_LOADING_MESSAGE,
}: LoadingDialogProps) {
  // Flag to indicate minimum loading duration has been reached
  const [reachedMinDuration, setReachedMinDuration] = useState(false)

  // Update flag after some duration
  useEffect(() => {
    let timeout = null
    if (isOpen) {
      // Start the minimum duration timer if dialog is open
      timeout = setTimeout(() => {
        setReachedMinDuration(true)
      }, minDuration)
    } else {
      // Reset flag when dialog is closed
      setReachedMinDuration(false)
    }

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Invoke onNext only if it has reached minimum loading duration
  useEffect(() => {
    if (reachedMinDuration) {
      onNext()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachedMinDuration])

  return (
    <Dialog isOpen={isOpen} closeOnOverlayClick={false} size="sm">
      <DialogOverlay />
      <DialogContent
        aria-labelledby="kirim-pegawai-loading-dialog-title"
        aria-describedby="kirim-pegawai-loading-dialog-description"
      >
        <div className="pt-20 pb-12 px-6 flex flex-col items-center text-center">
          <span className="mb-9">
            <Spinner size="lg" />
          </span>
          <Text
            variant="display-sm"
            as="h2"
            className="mb-1"
            id="kirim-pegawai-loading-dialog-title"
          >
            {loadingMessage}
          </Text>
          <Text
            variant="body-lg"
            color="subdued"
            id="kirim-pegawai-loading-dialog-description"
          >
            Mohon tunggu sebentar, ya.
          </Text>
        </div>
      </DialogContent>
    </Dialog>
  )
}

type SuccessDialogProps = {
  loadingMessage?: string
  successMessage?: string
  isOpen: boolean
  count: number
  onNext: () => void
}
function SuccessDialog({
  isOpen,
  count,
  onNext,
  successMessage = DEFAULT_SUCCESS_MESSAGE,
}: SuccessDialogProps) {
  return (
    <Dialog isOpen={isOpen} size="sm" closeOnOverlayClick={false}>
      <DialogOverlay />
      <DialogContent
        aria-labelledby="kirim-pegawai-success-dialog-title"
        aria-describedby="kirim-pegawai-success-dialog-description"
      >
        <div className="p-6">
          <img
            src={assetUrl('/illustrations/img-thumbs-up.png')}
            alt=""
            width={84}
            height={84}
            className="mb-4"
          />

          <div className="mb-6">
            <Text
              variant="display-sm"
              as="h2"
              className="mb-2"
              id="kirim-pegawai-success-dialog-title"
            >
              {count} {successMessage}
            </Text>

            <Text
              variant="body-lg"
              color="subdued"
              id="kirim-pegawai-success-dialog-description"
            >
              Anda dapat melanjutkan pengiriman jika ada yang belum dikirim.
              Mohon cek secara berkala.
            </Text>
          </div>

          <Button color="blue" size="lg" type="button" onClick={onNext}>
            Saya mengerti
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
